import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PaperPlaneTilt, Check, WarningCircle, TrendUp } from "@phosphor-icons/react";

/**
 * DropoffQueue — V1.1 #5.
 * Lists candidate users who signed up 24h+ ago but never uploaded a CV.
 * Admin can multi-select + fire an "email + WhatsApp reminder" batch.
 *
 * Backed by Superadmin Growth Dashboard finding: 100% signup → CV upload
 * drop-off. This page turns that funnel step into a manual reactivation
 * lever.
 */
export default function DropoffQueue() {
    const [rows, setRows] = useState([]);
    const [stats, setStats] = useState({ total_stalled: 0, nudged_last_24h: 0, recovered_last_7d: 0, cooldown_hours: 72 });
    const [selected, setSelected] = useState(new Set());
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        api.get("/admin/dropoff-queue")
            .then((r) => {
                setRows(r.data.items || []);
                setStats({
                    total_stalled: r.data.total_stalled || 0,
                    nudged_last_24h: r.data.nudged_last_24h || 0,
                    recovered_last_7d: r.data.recovered_last_7d || 0,
                    cooldown_hours: r.data.cooldown_hours || 72,
                });
                setSelected(new Set());
            })
            .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load drop-off queue"))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { load(); }, [load]);

    const toggle = (id) => {
        const next = new Set(selected);
        next.has(id) ? next.delete(id) : next.add(id);
        setSelected(next);
    };

    const toggleAll = () => {
        if (selected.size === rows.length) setSelected(new Set());
        else setSelected(new Set(rows.map((r) => r.id)));
    };

    const sendNudge = async (ids) => {
        if (!ids.length) return;
        setSending(true);
        try {
            const { data } = await api.post("/admin/dropoff-queue/nudge", { user_ids: ids });
            const parts = [`${data.sent} reminder${data.sent === 1 ? "" : "s"} sent`];
            if (data.failed) parts.push(`${data.failed} failed`);
            if (data.skipped_no_email) parts.push(`${data.skipped_no_email} skipped (no email)`);
            toast.success(parts.join(" · "));
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send reminders");
        } finally {
            setSending(false);
        }
    };

    const sendSelected = () => sendNudge([...selected]);

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1400px] mx-auto" data-testid="dropoff-queue-page">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Growth · Signup drop-off
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                        Drop-off queue.
                    </h1>
                    <p className="text-sm text-[#525252] mt-2 max-w-2xl">
                        Candidates who signed up 24+ hours ago but haven&apos;t uploaded a CV.
                        Send an email + WhatsApp reminder to pull them back in.
                        Cooldown: {stats.cooldown_hours}h between nudges.
                    </p>
                </div>
                <button
                    onClick={sendSelected}
                    disabled={sending || !selected.size}
                    data-testid="dropoff-send-selected"
                    className="self-start sm:self-auto bg-cobalt hover:bg-cobalt-dark disabled:opacity-40 disabled:cursor-not-allowed text-white px-5 py-2.5 sm:px-6 sm:py-3 font-bold tracking-wide text-sm inline-flex items-center gap-2"
                >
                    <PaperPlaneTilt size={14} weight="bold" />
                    {sending ? "Sending…" : `Nudge ${selected.size || ""} candidate${selected.size === 1 ? "" : "s"}`}
                </button>
            </div>

            {/* Stats strip */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <StatCard
                    icon={WarningCircle}
                    label="Stalled at CV upload"
                    value={stats.total_stalled}
                    tone="text-[#B45309] bg-[#FEF3C7]"
                    testid="dropoff-stat-stalled"
                />
                <StatCard
                    icon={PaperPlaneTilt}
                    label="Nudged in last 24h"
                    value={stats.nudged_last_24h}
                    tone="text-[#3730A3] bg-[#EEF2FF]"
                    testid="dropoff-stat-nudged"
                />
                <StatCard
                    icon={TrendUp}
                    label="Recovered (last 7d)"
                    value={stats.recovered_last_7d}
                    tone="text-[#059669] bg-[#ECFDF5]"
                    testid="dropoff-stat-recovered"
                />
            </div>

            <div className="bg-white border border-[#E5E5E5]" data-testid="dropoff-list">
                <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-[#E5E5E5] text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                    <div className="col-span-1">
                        <input
                            type="checkbox"
                            checked={rows.length > 0 && selected.size === rows.length}
                            onChange={toggleAll}
                            data-testid="dropoff-select-all"
                        />
                    </div>
                    <div className="col-span-4">Candidate</div>
                    <div className="col-span-2">Signed up</div>
                    <div className="col-span-2">Last nudged</div>
                    <div className="col-span-1 text-right">Nudges</div>
                    <div className="col-span-2 text-right">Action</div>
                </div>
                {loading && (
                    <div className="p-8 text-center text-sm text-[#525252]" data-testid="dropoff-loading">
                        Loading…
                    </div>
                )}
                {!loading && rows.length === 0 && (
                    <div className="p-10 text-center" data-testid="dropoff-empty">
                        <Check size={28} weight="bold" className="mx-auto text-[#059669] mb-2" />
                        <p className="font-heading font-black text-lg tracking-tight">All caught up.</p>
                        <p className="text-xs text-[#525252] mt-1">
                            No stalled candidates outside the {stats.cooldown_hours}h cooldown right now.
                        </p>
                    </div>
                )}
                {!loading && rows.map((r) => (
                    <div
                        key={r.id}
                        data-testid={`dropoff-row-${r.id}`}
                        className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-4 sm:px-6 py-3 border-b border-[#E5E5E5] last:border-b-0 items-center"
                    >
                        <div className="col-span-1">
                            <input
                                type="checkbox"
                                checked={selected.has(r.id)}
                                onChange={() => toggle(r.id)}
                                data-testid={`dropoff-select-${r.id}`}
                            />
                        </div>
                        <div className="col-span-4 min-w-0">
                            <p className="font-bold text-ink truncate">{r.name || "—"}</p>
                            <p className="text-xs text-[#525252] truncate">{r.email}</p>
                            {r.phone && <p className="text-[10px] text-[#A3A3A3]">{r.phone}</p>}
                        </div>
                        <div className="col-span-2 text-xs text-[#525252]">
                            {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                            {r.days_since_signup != null && (
                                <span className="ml-2 text-[10px] text-[#A3A3A3]">
                                    ({r.days_since_signup}d ago)
                                </span>
                            )}
                        </div>
                        <div className="col-span-2 text-xs text-[#525252]">
                            {r.last_nudged_at ? new Date(r.last_nudged_at).toLocaleDateString() : "Never"}
                        </div>
                        <div className="col-span-1 text-right text-xs font-bold">
                            {r.nudged_count || 0}
                        </div>
                        <div className="col-span-2 flex justify-start sm:justify-end">
                            <button
                                onClick={() => sendNudge([r.id])}
                                disabled={sending}
                                data-testid={`dropoff-nudge-${r.id}`}
                                className="text-xs font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1 disabled:opacity-50"
                            >
                                <PaperPlaneTilt size={12} weight="bold" /> Send reminder
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value, tone, testid }) {
    return (
        <div className={`p-4 ${tone}`} data-testid={testid}>
            <div className="flex items-start gap-3">
                <Icon size={20} weight="fill" />
                <div>
                    <p className="text-[10px] font-black uppercase tracking-wider">
                        {label}
                    </p>
                    <p className="font-heading text-3xl font-black tracking-tighter mt-0.5">
                        {value}
                    </p>
                </div>
            </div>
        </div>
    );
}
